import React from "react";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Badge from "../../components/ui-components/Badge";

// Shared control-check findings rendering — extracted from
// OrgRoleWizard/StepControlCheck.jsx (Sub-project E2) so Task 4's
// compare-with-template adoption flow (TemplateDiff/) can render the exact
// same severity-grouped findings list + override-reason gate for its own
// 409 ROLE_CONTROL_CHECK_FAILED (adopting a template's offered changes can
// trip the same rolecontrol engine as activating a role) instead of a second
// copy of this UI drifting from the original.

export const SEVERITY_ORDER = ["critical", "warning", "info"];

// Full, statically-analyzable Tailwind class strings per severity — never
// build these via template-literal interpolation (`text-${x}`), Tailwind's
// scanner can't see through that and would drop the class entirely.
const SEVERITY_META = {
  critical: { label: "Critical", badgeStatus: "danger" },
  warning: { label: "Warning", badgeStatus: "warning" },
  info: { label: "Info", badgeStatus: "info" },
};

// icon()'s name/style args must be string literals (it's a compile-time
// babel-macro, not a runtime function) — so each severity gets its own tiny
// component rather than one that takes the icon name as a variable.
export function SeverityIcon({ severity }) {
  if (severity === "critical") {
    return <FontAwesomeIcon icon={icon({ name: "circle-exclamation", style: "solid" })} className="mt-0.5 text-danger" />;
  }
  if (severity === "warning") {
    return <FontAwesomeIcon icon={icon({ name: "triangle-exclamation", style: "solid" })} className="mt-0.5 text-warning" />;
  }
  return <FontAwesomeIcon icon={icon({ name: "circle-info", style: "solid" })} className="mt-0.5 text-primary" />;
}

export function FindingRow({ finding }) {
  return (
    <div className="flex items-start gap-2.5 py-2.5">
      <SeverityIcon severity={finding.severity} />
      <div className="min-w-0 flex-1">
        <div className="text-sm text-text">{finding.detail}</div>
        {finding.subjects && finding.subjects.length > 0 && (
          <div className="text-xs text-text-2 mt-0.5">{finding.subjects.join(", ")}</div>
        )}
      </div>
    </div>
  );
}

// Groups a flat findings array by severity (critical -> warning -> info),
// dropping empty groups — the shape both StepControlCheck and
// TemplateDiffModal render as a "Critical · N" / "Warning · N" badge
// followed by that severity's FindingRows.
export function groupFindingsBySeverity(findings = []) {
  return SEVERITY_ORDER.map((sev) => ({
    severity: sev,
    items: findings.filter((f) => f.severity === sev),
  })).filter((g) => g.items.length > 0);
}

// The findings list itself: severity badges + rows, or a "No issues found"
// success line when there's nothing to show (only rendered when not
// currently loading/erroring — callers handle those states themselves since
// their loading/error copy differs by context).
export function ControlCheckFindingsList({ findings = [] }) {
  const grouped = groupFindingsBySeverity(findings);

  if (grouped.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-success">
        <FontAwesomeIcon icon={icon({ name: "circle-check", style: "solid" })} />
        No issues found.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="control-check-findings">
      {grouped.map((g) => {
        const meta = SEVERITY_META[g.severity];
        return (
          <div key={g.severity}>
            <div className="flex items-center gap-2 mb-1">
              <Badge status={meta.badgeStatus} label={`${meta.label} · ${g.items.length}`} size="sm" />
            </div>
            <div className="divide-y divide-border">
              {g.items.map((f, i) => (
                <FindingRow key={`${f.code}-${i}`} finding={f} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
